import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Send, Slash, Bell, AlertTriangle } from 'lucide-react';

/**
 * Notify-customer confirmation dialog used by Schedule publish/edit flows.
 *
 * mode:
 *   - "publish_first"   First time a shift is being published. Default = Send confirmation.
 *   - "publish_resave"  Already-published shift re-saved. Default = Silent (no UI shown,
 *                       caller should bypass and save silently). Kept here for completeness.
 *   - "reschedule"      Customer-facing field (date/time/address) changed. Default = Do Not Send.
 *
 * onConfirm(notify: boolean) → caller saves with notify_customer flag accordingly.
 */
export const NotifyCustomerDialog = ({ open, mode, onConfirm, onCancel, previewText, customerName }) => {
  const isReschedule = mode === 'reschedule';
  const title = isReschedule
    ? 'Customer-facing details changed'
    : 'Notify customer?';
  const description = isReschedule
    ? `You changed the date, time, or address on this published shift${customerName ? ` for ${customerName}` : ''}. Would you like to send the customer a schedule-update SMS?`
    : `This is the first time you're publishing this shift${customerName ? ` for ${customerName}` : ''}. Send the appointment confirmation SMS?`;
  const sendBtnLabel = isReschedule ? 'Send Update' : 'Send Confirmation';
  const skipBtnLabel = isReschedule ? 'Do Not Send' : 'Publish Silently';
  const Icon = isReschedule ? AlertTriangle : Bell;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="notify-customer-dialog" className="bg-[#141414] border border-[#262626] text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Icon className={`w-5 h-5 ${isReschedule ? 'text-amber-400' : 'text-[#D4AF37]'}`} />
            {title}
          </DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">{description}</DialogDescription>
        </DialogHeader>
        {previewText && (
          <div className="space-y-1.5">
            <p className="text-[#525252] text-[10px] uppercase tracking-wide">Customer will receive</p>
            <div
              data-testid="notify-customer-preview"
              className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 text-white text-sm leading-relaxed"
            >
              {previewText}
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 flex-col sm:flex-row">
          {isReschedule ? (
            <>
              <Button
                data-testid="notify-customer-skip"
                onClick={() => onConfirm(false)}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] flex-1"
              >
                <Slash className="w-4 h-4 mr-1.5" /> {skipBtnLabel}
              </Button>
              <Button
                data-testid="notify-customer-send"
                onClick={() => onConfirm(true)}
                variant="outline"
                className="border-amber-700 text-amber-300 hover:bg-amber-500/10 flex-1"
              >
                <Send className="w-4 h-4 mr-1.5" /> {sendBtnLabel}
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="notify-customer-send"
                onClick={() => onConfirm(true)}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] flex-1"
              >
                <Send className="w-4 h-4 mr-1.5" /> {sendBtnLabel}
              </Button>
              <Button
                data-testid="notify-customer-skip"
                onClick={() => onConfirm(false)}
                variant="outline"
                className="border-[#262626] text-[#A3A3A3] hover:bg-[#1f1f1f] hover:text-white flex-1"
              >
                <Slash className="w-4 h-4 mr-1.5" /> {skipBtnLabel}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NotifyCustomerDialog;
